(() => {
  const STORAGE_PREFIX = 'alwaysunder.afterHours.';

  const getKlaviyo = () => {
    window._klOnsite = window._klOnsite || [];

    if (window.klaviyo) return window.klaviyo;

    try {
      window.klaviyo = new Proxy({}, {
        get(_target, method) {
          if (method === 'push') {
            return (...args) => window._klOnsite.push(...args);
          }

          return (...args) => {
            const callback = typeof args[args.length - 1] === 'function' ? args.pop() : null;

            return new Promise((resolve) => {
              window._klOnsite.push([method, ...args, (result) => {
                callback?.(result);
                resolve(result);
              }]);
            });
          };
        }
      });
    } catch (_error) {
      window.klaviyo = window.klaviyo || [];
      window.klaviyo.push = (...args) => window._klOnsite.push(...args);
    }

    return window.klaviyo;
  };

  const callKlaviyo = (method, ...args) => {
    try {
      const klaviyo = getKlaviyo();

      if (klaviyo && typeof klaviyo[method] === 'function') {
        const result = klaviyo[method](...args);
        result?.catch?.(() => {});
        return true;
      }

      if (klaviyo && typeof klaviyo.push === 'function') {
        klaviyo.push([method, ...args]);
        return true;
      }
    } catch (_error) {
      // Klaviyo must never interrupt the reading experience.
    }

    return false;
  };

  const klaviyoTrackingAllowed = () => {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy) return false;

    const analyticsAllowed = typeof privacy.analyticsProcessingAllowed === 'function'
      ? privacy.analyticsProcessingAllowed()
      : true;
    const marketingAllowed = typeof privacy.marketingAllowed === 'function'
      ? privacy.marketingAllowed()
      : true;

    return analyticsAllowed && marketingAllowed;
  };

  const safeParse = (value, fallback) => {
    try {
      return value ? JSON.parse(value) : fallback;
    } catch (_error) {
      return fallback;
    }
  };

  const getState = (storyId) => {
    const fallback = {
      started: false,
      chapter: 1,
      route: null,
      choices: {},
      completed: false,
      tracked: []
    };

    try {
      return {
        ...fallback,
        ...safeParse(window.localStorage.getItem(`${STORAGE_PREFIX}${storyId}`), fallback)
      };
    } catch (_error) {
      return fallback;
    }
  };

  const saveState = (storyId, state) => {
    try {
      window.localStorage.setItem(`${STORAGE_PREFIX}${storyId}`, JSON.stringify(state));
    } catch (_error) {
      // Reading still works when storage is unavailable.
    }
  };

  const clearState = (storyId) => {
    try {
      window.localStorage.removeItem(`${STORAGE_PREFIX}${storyId}`);
    } catch (_error) {
      // Nothing else to do.
    }
  };

  const dispatchEvent = (metric, properties = {}) => {
    document.dispatchEvent(new CustomEvent('alwaysunder:after-hours', {
      detail: { metric, properties }
    }));

    if (!klaviyoTrackingAllowed()) return;
    callKlaviyo('track', metric, properties);
  };

  const trackOnce = (storyId, state, eventKey, metric, properties) => {
    if (state.tracked.includes(eventKey)) return;
    state.tracked.push(eventKey);
    saveState(storyId, state);
    dispatchEvent(metric, properties);
  };

  const openKlaviyoForm = (formId) => {
    if (!formId) return false;
    return callKlaviyo('openForm', formId);
  };

  const initFormTriggers = (scope = document) => {
    scope.querySelectorAll('[data-ah-open-form]').forEach((trigger) => {
      if (trigger.dataset.ahFormReady === 'true') return;
      trigger.dataset.ahFormReady = 'true';
      trigger.addEventListener('click', (event) => {
        const formId = trigger.dataset.ahOpenForm;
        if (openKlaviyoForm(formId)) event.preventDefault();
      });
    });
  };

  const initReader = (root) => {
    if (root.dataset.ahReaderReady === 'true') return;
    root.dataset.ahReaderReady = 'true';

    const storyId = root.dataset.storyId || 'story-01';
    const seasonId = root.dataset.seasonId || 'season-01';
    const storyTitle = root.dataset.storyTitle || '';
    const startFromBeginning = new URLSearchParams(window.location.search).get('start') === '1';
    let state = getState(storyId);

    const intro = root.querySelector('[data-ah-intro]');
    const progress = root.querySelector('[data-ah-progress]');
    const progressBar = root.querySelector('[data-ah-progress-bar]');
    const progressLabel = root.querySelector('[data-ah-progress-label]');
    const status = root.querySelector('[data-ah-status]');
    const chapters = [...root.querySelectorAll('[data-ah-chapter]')];
    const nightstand = root.querySelector('[data-ah-nightstand]');
    const routePanes = [...root.querySelectorAll('[data-ah-route]')];
    const choicePanes = [...root.querySelectorAll('[data-ah-choice-copy]')];
    const beginButton = root.querySelector('[data-ah-begin]');
    const beginLabel = root.querySelector('[data-ah-begin-label]');
    const resetButtons = [...root.querySelectorAll('[data-ah-reset]')];

    const announce = (message) => {
      if (!status) return;
      status.textContent = '';
      window.setTimeout(() => { status.textContent = message; }, 30);
    };

    const showRoute = (route) => {
      routePanes.forEach((pane) => {
        pane.hidden = pane.dataset.ahRoute !== route;
      });
    };

    const showChoiceCopy = (choiceId) => {
      choicePanes.forEach((pane) => {
        pane.hidden = pane.dataset.ahChoiceCopy !== choiceId;
      });
    };

    const showChapter = (chapterNumber, shouldFocus = false) => {
      const chapter = Math.min(Math.max(Number(chapterNumber) || 1, 1), chapters.length);
      state.chapter = chapter;
      saveState(storyId, state);

      if (intro) intro.hidden = true;
      if (progress) progress.hidden = false;
      if (nightstand) nightstand.hidden = !(state.completed && chapter === chapters.length);

      chapters.forEach((item) => {
        item.hidden = Number(item.dataset.ahChapter) !== chapter;
      });

      if (chapter === chapters.length && state.route) showRoute(state.route);
      if (chapter === 2) showChoiceCopy(state.choices.chapter_1);

      const percentage = Math.round((chapter / chapters.length) * 100);
      if (progressBar) progressBar.style.width = `${percentage}%`;
      if (progressLabel) progressLabel.textContent = `Chapter ${chapter} of ${chapters.length}`;

      if (shouldFocus) {
        const heading = chapter === chapters.length && state.route
          ? root.querySelector(`[data-ah-chapter="${chapter}"] [data-ah-route="${state.route}"] h2`)
          : root.querySelector(`[data-ah-chapter="${chapter}"] h2`);
        heading?.focus({ preventScroll: true });
        root.querySelector(`[data-ah-chapter="${chapter}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      announce(`Chapter ${chapter} of ${chapters.length}`);
    };

    const startStory = () => {
      state.started = true;
      saveState(storyId, state);
      trackOnce(storyId, state, 'story_started', 'AH Story Started', {
        story_id: storyId,
        season_id: seasonId,
        story_title: storyTitle
      });
      showChapter(state.chapter || 1, true);
    };

    const resetStory = () => {
      clearState(storyId);
      state = getState(storyId);
      chapters.forEach((chapter) => { chapter.hidden = true; });
      routePanes.forEach((pane) => { pane.hidden = true; });
      choicePanes.forEach((pane) => { pane.hidden = true; });
      if (nightstand) nightstand.hidden = true;
      if (progress) progress.hidden = true;
      if (intro) intro.hidden = false;
      if (beginLabel) beginLabel.textContent = beginLabel.dataset.startLabel || 'Begin chapter one';
      intro?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      announce('Your saved story progress has been cleared.');
    };

    if (state.started && beginLabel) {
      beginLabel.textContent = beginLabel.dataset.continueLabel || 'Continue where you left off';
    }

    beginButton?.addEventListener('click', startStory);
    resetButtons.forEach((button) => button.addEventListener('click', resetStory));

    root.querySelectorAll('[data-ah-choice]').forEach((button) => {
      button.addEventListener('click', () => {
        const chapter = Number(button.dataset.chapter);
        const choiceId = button.dataset.ahChoice;
        const route = button.dataset.route || null;
        const nextChapter = Number(button.dataset.nextChapter || chapter + 1);

        state.choices[`chapter_${chapter}`] = choiceId;
        if (route) state.route = route;

        trackOnce(storyId, state, `choice_${chapter}`, 'AH Choice Made', {
          story_id: storyId,
          season_id: seasonId,
          chapter_id: `chapter_${chapter}`,
          choice_id: choiceId
        });

        trackOnce(storyId, state, `chapter_${chapter}_completed`, 'AH Chapter Completed', {
          story_id: storyId,
          season_id: seasonId,
          chapter_id: `chapter_${chapter}`
        });

        if (nextChapter >= chapters.length && state.route) {
          state.completed = true;
          trackOnce(storyId, state, 'story_completed', 'AH Story Completed', {
            story_id: storyId,
            season_id: seasonId,
            ending_id: `${state.route}_ending`,
            route_id: state.route
          });
        }

        saveState(storyId, state);
        showChapter(nextChapter, true);
      });
    });

    root.querySelectorAll('[data-ah-product-link]').forEach((link) => {
      link.addEventListener('click', () => {
        dispatchEvent('AH Nightstand Clicked', {
          story_id: storyId,
          season_id: seasonId,
          route_id: link.dataset.routeId,
          product_handle: link.dataset.productHandle,
          position: Number(link.dataset.position || 0)
        });
      });
    });

    root.querySelectorAll('[data-ah-other-ending]').forEach((button) => {
      button.addEventListener('click', () => {
        state.route = state.route === 'route_a' ? 'route_b' : 'route_a';
        state.completed = true;
        saveState(storyId, state);
        showRoute(state.route);
        if (nightstand) nightstand.hidden = false;
        root.querySelector('[data-ah-chapter="3"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        announce('The other ending is now open.');
      });
    });

    if (startFromBeginning) {
      clearState(storyId);
      state = getState(storyId);

      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('start');
      window.history.replaceState({}, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);

      startStory();
    } else if (state.started) {
      showChapter(state.chapter || 1, false);
    } else {
      chapters.forEach((chapter) => { chapter.hidden = true; });
      if (progress) progress.hidden = true;
      if (nightstand) nightstand.hidden = true;
      if (intro) intro.hidden = false;
    }
  };

  const boot = (scope = document) => {
    initFormTriggers(scope);
    scope.querySelectorAll('[data-ah-reader]').forEach(initReader);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
